import clsx from 'clsx'
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, CircleCheckIcon, HelpCircleIcon, PhoneIcon, XCircleIcon } from 'lucide-react'
import type React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { Badge, Popover, TextButton, TextLink } from 'slab'
import type { MemberOnboardingInNetworkVerificationRecordInNetworkVerificationProgressStatus, MemberOnboardingProviderInNetworkMap, MemberOnboardingProviderInNetworkMapInNetwork } from 'utils/web_api.schemas'

import { useProviderInNetworkVerificationContext } from './ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'
import { useProviderInNetworkVerificationResults } from './ProviderInNetworkVerificationResults/useProviderInNetworkVerificationResults'

export const InNetworkStatusIcon: React.FC<{
  status: MemberOnboardingProviderInNetworkMapInNetwork
  size?: 'small'
}> = ({ status, size }) => {
  const iconSize = size === 'small' ? 12 : 14
  switch (status) {
    case 'IN_NETWORK':
      return (
        <CircleCheckIcon className='text-green-400' size={iconSize}>
          <title>In network</title>
        </CircleCheckIcon>
      )
    case 'OUT_OF_NETWORK':
      return (
        <XCircleIcon className='text-red-400' size={iconSize}>
          <title>Out of network</title>
        </XCircleIcon>
      )
    default:
      return (
        <HelpCircleIcon className='text-yellow-400' size={iconSize}>
          <title>Unknown</title>
        </HelpCircleIcon>
      )
  }
}

const LaunchFerryVerificationTaskTakeover: React.FC<{
  launchModal: () => void;
}> = ({ launchModal }) => {
  return (
    <div className='body-sm'>
      <span className='text-gray-500'>
        You can try calling your providers, or we can help.{' '}
      </span>
      <TextButton data-testid='verify-link' onClick={launchModal} rightIcon={ArrowRightIcon}>
        <span className='text-gray-800'>
          Get started
        </span>
      </TextButton>
    </div>
  )
}

const SelfServeInNetworkVerificationLink: React.FC<{
  verifyingInNetworkStatusPlanIds: string[];
  planId: string;
  inNetworkVerificationProgressStatus?: MemberOnboardingInNetworkVerificationRecordInNetworkVerificationProgressStatus;
  providerSupportLink?: string;
  isNearEnrollmentEnd: boolean
}> = ({ verifyingInNetworkStatusPlanIds, planId, inNetworkVerificationProgressStatus, providerSupportLink, isNearEnrollmentEnd }) => {
  if (verifyingInNetworkStatusPlanIds.includes(planId)) return null
  if (isNearEnrollmentEnd || verifyingInNetworkStatusPlanIds.length === 0 || inNetworkVerificationProgressStatus === 'done') {
    return (
      <div className='body-sm'>
        <span className='text-gray-500'>You can try to verify. </span>
        <TextLink data-testid='support-link' size='small' href={providerSupportLink} target='_blank'>
          <span className='text-gray-800'>
            Learn how
          </span>
        </TextLink>
      </div>
    )
  }

  const label = `We're calling your providers on ${verifyingInNetworkStatusPlanIds.length} other plans. In the meantime, you can try `

  return (
    <div className='body-sm'>
      <span className='text-gray-500'>{label}</span>
      <TextLink data-testid='support-link' size='small' href={providerSupportLink} target='_blank'>
        <span className='text-gray-800'>
          verifying this one on your own.
        </span>
      </TextLink>
    </div>
  )
}

export const InNetworkVerificationProgressBadge: React.FC<{
  inNetworkVerificationProgressStatus?: MemberOnboardingInNetworkVerificationRecordInNetworkVerificationProgressStatus;
}> = ({ inNetworkVerificationProgressStatus }) => {
  if (inNetworkVerificationProgressStatus === 'done') {
    return <Badge bordered type='thatch' leftIcon={CheckIcon}>Provider calls completed</Badge>
  }
  if (inNetworkVerificationProgressStatus === 'in_progress') {
    return <Badge bordered type='thatch' leftIcon={PhoneIcon}>Provider calls in progress</Badge>
  }
  return null
}

export const ProviderInNetworkVerification: React.FC<{
  providerInNetworkMap: MemberOnboardingProviderInNetworkMap[];
  providerSupportLink?: string;
  planId: string;
  defaultOpen?: boolean;
  showInNetworkVerificationCta?: boolean;
  onLaunchVerificationModal?: () => void;
  onSelectMorePlansForVerification?: () => void;
}> = ({ providerInNetworkMap, providerSupportLink, onLaunchVerificationModal, onSelectMorePlansForVerification, planId, defaultOpen, showInNetworkVerificationCta = true }) => {
  const [open, setOpen] = useState<boolean>(defaultOpen ?? false)

  const { badge, label, status, content, canKickOffInNetworkVerificationTask, verifyingInNetworkStatusPlanIds } = useProviderInNetworkVerificationResults({
    planId,
    providerInNetworkMap,
    withDividers: true,
  })
  const { isNearEnrollmentEnd, launchModal, inNetworkVerificationProgressStatus } = useProviderInNetworkVerificationContext()
  const allProvidersInNetwork = useMemo(() => {
    return providerInNetworkMap.every(p => p.in_network === 'IN_NETWORK')
  }, [providerInNetworkMap])

  const handleLaunchModal = useCallback(() => {
    launchModal({
      planIds: [planId],
      onSelectMorePlansForVerification
    })
    onLaunchVerificationModal?.()
    setOpen(false)
  }, [launchModal, onLaunchVerificationModal, onSelectMorePlansForVerification, planId])

  return (
    <div className='flex flex-col gap-2'>
      <Popover
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={setOpen}
        trigger={
          <button
            className={
              clsx(
                'flex gap-1 items-center body-bold text-gray-500 hover:text-gray-800 focus-visible:text-gray-800 rounded-md',
                'outline outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500',
                'transition-all'
              )
            }
          >
            <InNetworkStatusIcon status={status} />
            <span>{label}</span>
            <span className='text-gray-400 group-hover/provider-trigger:text-gray-500 group-focus-visible/provider-trigger:text-gray-500 transition'>
              {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
            </span>
          </button>
        }
        zIndex={50}
      >
        <div className='p-4 flex flex-col gap-2 w-[250px]'>
          {content}
          {
            !allProvidersInNetwork && showInNetworkVerificationCta && (
              canKickOffInNetworkVerificationTask
                ? (
                  <LaunchFerryVerificationTaskTakeover launchModal={handleLaunchModal} />
                  )
                : <SelfServeInNetworkVerificationLink
                    verifyingInNetworkStatusPlanIds={verifyingInNetworkStatusPlanIds}
                    planId={planId}
                    inNetworkVerificationProgressStatus={inNetworkVerificationProgressStatus}
                    providerSupportLink={providerSupportLink}
                    isNearEnrollmentEnd={isNearEnrollmentEnd}
                  />
            )
          }
        </div>
      </Popover>
      {badge}
    </div>
  )
}
